// promomanagermodal.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Badge, Spinner, Alert, Tabs, Tab } from 'react-bootstrap';
import { useSelector } from 'react-redux';

// Hook e servizi
import useVetrina from '../../hooks/useVetrina';
import useSconti from '../../hooks/useSconti';
import useCoupon from '../../hooks/useCoupon';
import { alertService } from '../../services/AlertService';
import UtilityService from '../../services/UtillityServices/UtilityService';

const PromoManagerModal = ({ show, onHide, corsoVetrina, onUpdate }) => {
  const userLoggedIn = useSelector(state => state.user.userData);

  // Hooks remoti
  const { getScontoByIdCorso } = useSconti();
  const { isLoading: isLoadingVetrina, getPromozioni, createPromozione, deletePromozione } = useVetrina();
  const { isLoading: isLoadingCoupon, createCouponCampaign, deleteCouponCampaign } = useCoupon();

  // Stato UI
  const [promozioni, setPromozioni] = useState([]);
  const [maxSconto, setMaxSconto] = useState(0);
  const [activeTab, setActiveTab] = useState('sconto-generico'); // default
  const [formData, setFormData] = useState({
    // stato iniziale coerente con la tab default
    data_inizio: '',
    data_fine: '',
    sconto: '',
    note: ''
  });

  // Modelli iniziali per reset tab-specifico
  const initialGeneric = {
    data_inizio: '',
    data_fine: '',
    sconto: '',
    note: ''
  };

  const initialCoupon = {
    nome_campagna: '',
    codice_coupon: '',
    quantita_coupon: '',
    sconto: '',
    data_inizio: '',
    data_fine: '',
    note: ''
  };

  // Loading combinato
  const isLoading = isLoadingVetrina || isLoadingCoupon;

  // Fetch dati (promozioni + margine sconto)
  const fetchAllData = async () => {
    if (show && corsoVetrina?.id_classi_superpartner) {
      const [promoData, scontoData] = await Promise.all([
        getPromozioni(corsoVetrina.id_classi_superpartner),
        getScontoByIdCorso({ id_corso: corsoVetrina.id_corso, id_utente: userLoggedIn.id_utente })
      ]);
      setPromozioni(promoData);
      setMaxSconto(parseFloat(scontoData?.sconto || 0));
    }
  };

  useEffect(() => {
    fetchAllData();
    // Quando apro il modal, resetto la tab e lo stato form
    if (show) {
      setActiveTab('sconto-generico');
      setFormData(initialGeneric);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, corsoVetrina]);

  // Quando cambia tab, resetto lo schema del form per evitare residui
  useEffect(() => {
    setFormData(activeTab === 'coupon' ? initialCoupon : initialGeneric);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Gestione input controllati
  const handleFormChange = (e) => {
    const { name, value, type } = e.target;

    // Per i number manteniamo comunque stringa nello stato (così gli input si comportano bene)
    // e convertiamo solo al momento del submit.
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? value : value
    }));
  };

  // Validazioni minime lato client (non invasive)
  const validateBeforeSave = () => {
    const scontoRichiesto = parseFloat(formData.sconto || '0');

    if (Number.isNaN(scontoRichiesto)) {
      alertService.error('Inserisci un valore di sconto valido.');
      return false;
    }
    if (scontoRichiesto > maxSconto) {
      alertService.error(`Lo sconto non può superare il margine di €${maxSconto.toFixed(2)}.`);
      return false;
    }

    // Date minime
    if (!formData.data_inizio || !formData.data_fine) {
      alertService.error('Compila le date di inizio e fine.');
      return false;
    }

    // Campi specifici per coupon
    if (activeTab === 'coupon') {
      if (!formData.nome_campagna || !formData.codice_coupon) {
        alertService.error('Inserisci nome campagna e codice coupon.');
        return false;
      }
      const qta = parseInt(formData.quantita_coupon || '0', 10);
      if (Number.isNaN(qta) || qta <= 0) {
        alertService.error('La quantità utilizzi deve essere un numero positivo.');
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) return;

    // Preparo il payload in base alla tab
    const base = {
      id_classi_superpartner: corsoVetrina.id_classi_superpartner,
      data_inizio: formData.data_inizio,
      data_fine: formData.data_fine,
      sconto: formData.sconto // lascio stringa; il backend accetta DECIMAL/STRING
    };

    let success = false;

    if (activeTab === 'coupon') {
      const payload = {
        ...base,
        tipo_promo: 'Coupon',
        nome_campagna: (formData.nome_campagna || '').trim(),
        codice_coupon: (formData.codice_coupon || '').trim(),
        quantita_coupon: parseInt(formData.quantita_coupon || '0', 10),
        note: formData.note || ''
      };

      success = await createCouponCampaign(payload);
    } else {
      const payload = {
        ...base,
        tipo_promo: 'Sconto Generico',
        note: formData.note || ''
      };

      success = await createPromozione(payload);
    }

    if (success) {
      // Reset preciso in base alla tab
      setFormData(activeTab === 'coupon' ? initialCoupon : initialGeneric);

      // Ricarico lista promozioni
      fetchAllData();

      // Notifica al parent se serve
      if (onUpdate) onUpdate();
    }
  };

  const handleDelete = async (promo) => {
    if (window.confirm('Sei sicuro di voler eliminare questa promozione?')) {
      let success = false;
      if (promo.tipo_promo === 'Coupon') {
        success = await deleteCouponCampaign(promo.id_classi_superpartner_promo);
      } else {
        success = await deletePromozione(promo.id_classi_superpartner_promo);
      }
      if (success) {
        fetchAllData();
      }
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Gestione Promozioni: {corsoVetrina.denominazione_corso}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="info">
          Il tuo margine di sconto massimo per questo corso è di <strong>€ {maxSconto.toFixed(2)}</strong>.
        </Alert>

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
          <Tab eventKey="sconto-generico" title="Sconto Generico in Vetrina" mountOnEnter unmountOnExit>
            <div className="p-3 border border-top-0">
              <h6>Crea Sconto Generico</h6>
              <p className="text-muted small">
                Questo sconto sarà visibile a tutti nella tua vetrina per il periodo selezionato.
              </p>
              <div className="row g-2 align-items-end">
                <div className="col-md-3">
                  <Form.Label>Inizio</Form.Label>
                  <Form.Control
                    type="date"
                    name="data_inizio"
                    value={formData.data_inizio ?? ''}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="col-md-3">
                  <Form.Label>Fine</Form.Label>
                  <Form.Control
                    type="date"
                    name="data_fine"
                    value={formData.data_fine ?? ''}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="col-md-2">
                  <Form.Label>Sconto (€)</Form.Label>
                  <Form.Control
                    type="number"
                    name="sconto"
                    max={maxSconto}
                    value={formData.sconto ?? ''}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="col-md-3">
                  <Form.Label>Note</Form.Label>
                  <Form.Control
                    type="text"
                    name="note"
                    placeholder="Opzionale"
                    value={formData.note ?? ''}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="col-md-1">
                  <Button className="w-100" onClick={handleSave} disabled={isLoading}>
                    {isLoading ? <Spinner size="sm" /> : 'Crea'}
                  </Button>
                </div>
              </div>
            </div>
          </Tab>

          <Tab eventKey="coupon" title="Campagna Coupon" mountOnEnter unmountOnExit>
            <div className="p-3 border border-top-0">
              <h6>Crea Nuova Campagna Coupon</h6>
              <p className="text-muted small">
                Crea un codice coupon da distribuire. Lo sconto verrà applicato solo a chi inserisce il codice.
              </p>

              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <Form.Label>Nome Campagna</Form.Label>
                  <Form.Control
                    type="text"
                    name="nome_campagna"
                    value={formData.nome_campagna ?? ''}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="col-md-4">
                  <Form.Label>Codice Coupon</Form.Label>
                  <Form.Control
                    type="text"
                    name="codice_coupon"
                    value={formData.codice_coupon ?? ''}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="col-md-2">
                  <Form.Label>Quantità Utilizzi</Form.Label>
                  <Form.Control
                    type="number"
                    name="quantita_coupon"
                    value={formData.quantita_coupon ?? ''}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="col-md-2">
                  <Form.Label>Sconto (€)</Form.Label>
                  <Form.Control
                    type="number"
                    name="sconto"
                    max={maxSconto}
                    value={formData.sconto ?? ''}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="col-md-3">
                  <Form.Label>Data Inizio</Form.Label>
                  <Form.Control
                    type="date"
                    name="data_inizio"
                    value={formData.data_inizio ?? ''}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="col-md-3">
                  <Form.Label>Data Fine</Form.Label>
                  <Form.Control
                    type="date"
                    name="data_fine"
                    value={formData.data_fine ?? ''}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="col-md-4">
                  <Form.Label>Note</Form.Label>
                  <Form.Control
                    type="text"
                    name="note"
                    placeholder="Opzionale"
                    value={formData.note ?? ''}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="col-md-2">
                  <Button className="w-100" onClick={handleSave} disabled={isLoading}>
                    {isLoading ? <Spinner size="sm" /> : 'Crea'}
                  </Button>
                </div>
              </div>
            </div>
          </Tab>
        </Tabs>

        <hr />

        <h6>Promozioni Esistenti</h6>
        {isLoading && !promozioni.length ? (
          <div className="text-center"><Spinner /></div>
        ) : promozioni.length > 0 ? (
          <Table striped bordered size="sm">
            <thead className="table-light">
              <tr>
                <th>Tipo</th>
                <th>Dettagli Campagna</th>
                <th>Periodo</th>
                <th className="text-end">Sconto</th>
                <th className="text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {promozioni.map(promo => (
                <tr key={promo.id_classi_superpartner_promo}>
                  <td>
                    <Badge bg={promo.tipo_promo === 'Coupon' ? 'primary' : 'success'}>
                      {promo.tipo_promo}
                    </Badge>
                  </td>
                  <td>
                    {promo.tipo_promo === 'Coupon'
                      ? `${promo.nome_campagna} (${promo.codice_coupon})`
                      : 'Sconto generico'}
                  </td>
                  <td>
                    Dal {UtilityService.formatDate(promo.data_inizio)} al {UtilityService.formatDate(promo.data_fine)}
                  </td>
                  <td className="text-end">€ {parseFloat(promo.sconto).toFixed(2)}</td>
                  <td className="text-center">
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(promo)}>
                      <i className="bi bi-trash-fill"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Alert variant="secondary">Nessuna promozione attiva per questo corso.</Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Chiudi</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PromoManagerModal;
